import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  type AgentCredential,
  type AgentCredentialTestResult,
  type AgentSettings,
} from "../../../../api/client.ts";
import { keys } from "../../../../api/queryKeys.ts";
import ClaudeLogo from "../../../../components/ClaudeLogo.tsx";
import SecretCredentialForm from "../../../../components/SecretCredentialForm.tsx";
import AccordionCard from "../../../../components/AccordionCard.tsx";
import type { Status } from "../../../../components/StatusIcon.tsx";
import { withoutWhitespace } from "../../../../utils/secrets.ts";

// The organization's Claude session token (backend `CLAUDE_CODE_OAUTH_TOKEN`).
const CLAUDE_TOKEN_SLUG = "claude_code_oauth_token";

type TokenSource = "organization" | "environment" | "none";

function tokenSource(credential: AgentCredential): TokenSource {
  if (credential.stored) return "organization";
  return credential.env_fallback_available ? "environment" : "none";
}

// The field and its button sit on one line; the button takes a small field's height.

function TokenSettings({
  orgId,
  credential,
  canManage,
}: {
  orgId: string;
  credential: AgentCredential;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState("");
  const [testResult, setTestResult] = useState<AgentCredentialTestResult | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: keys.agentCredential.list(orgId) });

  const save = useMutation({
    mutationFn: () => api.setAgentCredential(orgId, credential.slug, withoutWhitespace(secret)),
    onSuccess: () => {
      setSecret("");
      setTestResult(null);
      invalidate();
      toast.success(`${credential.name} updated`);
    },
  });

  const clear = useMutation({
    mutationFn: () => api.clearAgentCredential(orgId, credential.slug),
    onSuccess: () => {
      setTestResult(null);
      invalidate();
      toast.success(`${credential.name} cleared`);
    },
  });

  const test = useMutation({
    mutationFn: () => api.testAgentCredential(orgId, credential.slug),
    onSuccess: (result) => setTestResult(result),
  });

  const source = tokenSource(credential);

  return (
    <SecretCredentialForm
      name={credential.name}
      description={credential.description}
      stored={credential.stored}
      hint={credential.hint}
      updatedAt={credential.stored ? credential.updated_at : null}
      updatedByEmail={credential.updated_by_email}
      changedLabel="Last rotated"
      encryptionConfigured={credential.encryption_configured}
      encryptionWarning={
        <>
          saving is refused rather than writing a live credential in clear text. Ask an operator to
          set it before rotating here.
        </>
      }
      canManage={canManage}
      value={secret}
      onChange={setSecret}
      fieldLabel={credential.stored ? "New token" : "Token"}
      placeholder="sk-ant-..."
      helperText="Write-only — the stored value is never shown again."
      onSave={() => save.mutate()}
      saving={save.isPending}
      saveLabel={credential.stored ? "Rotate" : "Save"}
      saveDisabled={!withoutWhitespace(secret) || !credential.encryption_configured}
      onTest={() => test.mutate()}
      testing={test.isPending}
      testDisabled={source === "none"}
      testResult={
        testResult && {
          ok: testResult.ok,
          detail: (
            <>
              {testResult.detail}
              {testResult.source !== "none" &&
                ` (tested the ${
                  testResult.source === "organization" ? "organization's" : "platform default"
                } token)`}
            </>
          ),
        }
      }
      onDismissTestResult={() => setTestResult(null)}
      onClear={() => clear.mutateAsync()}
      clearConfirm={{
        title: `Clear ${credential.name}`,
        body: (
          <>
            Clear this organization's token?{" "}
            {credential.env_fallback_available ? (
              <>
                Agent runs fall back to the platform default <code>{credential.env_var}</code> from
                the next run.
              </>
            ) : (
              "Its agent runs fail until a new token is set."
            )}
          </>
        ),
      }}
    >
      {source === "environment" && (
        <Alert severity="info">
          This organization has no token of its own, so runs fall back to the platform-wide{" "}
          <code>{credential.env_var}</code>. Save a token here to bill this organization's agent
          runs to its own Claude subscription — it takes effect from the next run.
        </Alert>
      )}

      {source === "none" && (
        <Alert severity="error">
          Agent runs for this organization will fail until a token is set here.
        </Alert>
      )}
    </SecretCredentialForm>
  );
}

function BudgetSettings({
  orgId,
  settings,
  canManage,
}: {
  orgId: string;
  settings: AgentSettings;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState<string | null>(null);

  // Only adopt the server value until the field is touched, so typing is not clobbered
  // by a refetch.
  const current = value ?? (settings.max_budget_usd != null ? String(settings.max_budget_usd) : "");

  const save = useMutation({
    mutationFn: () => {
      const trimmed = current.trim();
      return api.updateAgentSettings(orgId, trimmed === "" ? null : Number(trimmed));
    },
    onSuccess: () => {
      setValue(null);
      queryClient.invalidateQueries({ queryKey: keys.agentCredential.settings(orgId) });
      toast.success("Run budget updated");
    },
  });

  const capped = settings.max_budget_usd != null;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body1">
          Run budget:{" "}
          <strong>{capped ? `$${settings.max_budget_usd} per run` : "no cap"}</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          The most one agent run may spend against this organization's Claude subscription. A run
          that reaches the ceiling stops cleanly and keeps the prospects it already wrote.
          {!capped && " With no cap, a run is bounded only by its turn limit and a 4-hour timeout."}
        </Typography>
      </Box>

      {canManage && (
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <TextField
            label="Max USD per run"
            value={current}
            onChange={(e) => setValue(e.target.value)}
            type="number"
            size="small"
            fullWidth
            placeholder="e.g. 25"
            helperText="Empty removes the cap."
          />
          <Button
            variant="contained"
            onClick={() => save.mutate()}
            loading={save.isPending}
            sx={{ flexShrink: 0 }}
          >
            Save
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

/** The Claude subscription the organization's agents run on, and what one run may spend. */
export default function ClaudeIntegrationSection({
  organizationId,
  canManage,
}: {
  organizationId: string;
  // Organization admins (org:manage_credentials) set, rotate and test.
  canManage: boolean;
}) {
  const credentials = useQuery({
    queryKey: keys.agentCredential.list(organizationId),
    queryFn: () => api.getAgentCredentials(organizationId),
  });

  const settings = useQuery({
    queryKey: keys.agentCredential.settings(organizationId),
    queryFn: () => api.getAgentSettings(organizationId),
  });

  const credential = credentials.data?.find((c) => c.slug === CLAUDE_TOKEN_SLUG);
  const source = credential ? tokenSource(credential) : null;
  const cap = settings.data?.max_budget_usd ?? null;
  const capText = cap != null ? `$${cap} cap per run` : "No run cap";

  let status: Status;
  let summary: string;
  if (credentials.isLoading || settings.isLoading) {
    status = { kind: "loading" };
    summary = "Run this organization's agents on its own Claude subscription.";
  } else if (source === "organization") {
    status = { kind: "ok", label: "Connected" };
    summary = `${credential?.hint ? `Token …${credential.hint}` : "Token set"} · ${capText}`;
  } else if (source === "environment") {
    status = { kind: "ok", label: "Platform default" };
    summary = `Agent runs use the platform default token · ${capText}`;
  } else {
    status = { kind: "attention", label: "Not configured" };
    summary = "Add your organization's Claude session token so its agents can run.";
  }

  return (
    <AccordionCard
      logo={<ClaudeLogo />}
      title="Claude"
      summary={summary}
      status={status}
      defaultExpanded={source === "none"}
    >
      <Stack spacing={2} divider={<Divider flexItem />}>
        {credential && (
          <TokenSettings orgId={organizationId} credential={credential} canManage={canManage} />
        )}
        {settings.data && (
          <BudgetSettings orgId={organizationId} settings={settings.data} canManage={canManage} />
        )}
        {!canManage && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Only organization admins can set or rotate the token and the run budget.
          </Typography>
        )}
      </Stack>
    </AccordionCard>
  );
}
